import React, { useEffect } from "react";
import { Link, Navigate } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, ScrollText } from "lucide-react";
import { LEGAL_PAGES, LEGAL_FOOTER_LINKS } from "@/lib/legalContent";

export default function Legal({ page }) {
  const data = LEGAL_PAGES[page];
  useEffect(() => { window.scrollTo(0, 0); }, [page]);
  if (!data) return <Navigate to="/" replace />;

  return (
    <div className="min-h-screen bg-[#FBF7F1]">
      <header className="border-b border-[#E5E0D8] bg-white/80 backdrop-blur sticky top-0 z-10">
        <div className="max-w-3xl mx-auto px-5 h-16 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-[#5C574F] hover:text-[#E85D04] transition-colors" data-testid="legal-back-home">
            <ArrowLeft className="h-5 w-5" />
            <span className="font-semibold text-sm">Retour à l'accueil</span>
          </Link>
          <span className="font-display font-extrabold text-[#1B4332]">SuccèsNet</span>
        </div>
      </header>

      <motion.main
        initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.4 }}
        className="max-w-3xl mx-auto px-5 py-10"
        data-testid={`legal-page-${page}`}
      >
        <div className="flex items-center gap-2 mb-1">
          <span className="h-10 w-10 rounded-2xl bg-[#1B4332] flex items-center justify-center">
            <ScrollText className="h-5 w-5 text-white" />
          </span>
          <h1 className="font-display text-3xl sm:text-4xl font-extrabold tracking-tight text-[#1A1A1A]" data-testid="legal-title">{data.title}</h1>
        </div>
        <p className="text-xs text-[#9c968a] mb-6">Dernière mise à jour : {data.updated}</p>

        {data.intro && <p className="text-[#5C574F] leading-relaxed mb-8">{data.intro}</p>}

        <div className="space-y-8">
          {data.sections.map((sec, i) => (
            <section key={sec.heading}>
              <h2 className="font-display text-lg font-bold text-[#1B4332] mb-2">{sec.heading}</h2>
              {sec.text && <p className="text-[#5C574F] leading-relaxed whitespace-pre-line">{sec.text}</p>}
              {sec.bullets && (
                <ul className="mt-2 space-y-1.5">
                  {sec.bullets.map((b) => (
                    <li key={b} className="flex items-start gap-2 text-[#5C574F] leading-relaxed">
                      <span className="mt-2 h-1.5 w-1.5 rounded-full bg-[#E85D04] shrink-0" />
                      <span>{b}</span>
                    </li>
                  ))}
                </ul>
              )}
            </section>
          ))}
        </div>

        <nav className="mt-12 pt-6 border-t border-[#E5E0D8] flex flex-wrap gap-x-5 gap-y-2 text-sm">
          {LEGAL_FOOTER_LINKS.filter((l) => l.to !== `/${page}`).map((l) => (
            <Link key={l.to} to={l.to} className="text-[#5C574F] hover:text-[#E85D04] transition-colors" data-testid={`legal-link-${l.to.slice(1)}`}>
              {l.label}
            </Link>
          ))}
          <a href="mailto:support@succesnet.com" className="text-[#5C574F] hover:text-[#E85D04] transition-colors">support@succesnet.com</a>
        </nav>
      </motion.main>
    </div>
  );
}
